import { observer } from 'mobx-react-lite';
import React, { useContext } from 'react';
import { getAccountActivityRuleErrors } from '../../../../components/InputField/helper';
import { DialogContext } from '../../../../store/context/DialogContext';
import ButtonGroupNew from '../BusinessLiteForms/components/ButtonGroupNew';
import BusinessV2FieldGrid from './BusinessV2FieldGrid';
import {
    collectVisibleFieldErrors,
    getFieldErrorValue,
    getFirstErrorInFieldOrder,
    getVisibleWizardFieldOrder,
    mapRuleErrorsToFieldErrors,
    mergeClearedFieldErrors,
    omitFieldPathErrors
} from './helperV2';

const BusinessActivityPrime = ({
    asLabel,
    hideButtons,
    data,
    handleUpdate,
    hasError,
    setHasError,
    loading,
    cyclosFields,
    errors,
    setErrors,
    iPlusFields,
    wizard,
    handleChange,
    handlePrev,
    applicationForm,
    pageNum = 2,
}) => {
    const { showError } = useContext(DialogContext);
    const visibleFieldOrder = getVisibleWizardFieldOrder({
        fields: iPlusFields,
        wizard,
        data,
        type: Number(applicationForm?.type),
    });

    const handleFieldError = (field, errorMsg) => {
        setErrors((prevErrors) => ({
            ...(prevErrors || {}),
            [field]: errorMsg,
        }));
    };

    const checkNext = () => {
        const { fieldErrors } = collectVisibleFieldErrors({
            data,
            step: { wizard },
            iPlusFields,
            errors,
            type: Number(applicationForm?.type),
        });
        const mergedErrors = {
            ...(errors || {}),
            ...fieldErrors,
        };
        setErrors(mergedErrors);

        for (const fieldPath of visibleFieldOrder) {
            const fieldError = getFieldErrorValue(mergedErrors, fieldPath);
            if (fieldError) {
                setHasError(true);
                showError(fieldError);
                return false;
            }
        }

        const crossFieldErrors = getAccountActivityRuleErrors(data, {
            depositLocationFields: ["Deposit_Locations"],
            paymentLocationFields: ["Payment_Locations"],
            skipWhenInterestedInPrime: false,
            deferLocationErrorsUntilActivityFieldsFilled: true,
        });
        if (crossFieldErrors.length) {
            setHasError(true);
            const mappedErrors = mapRuleErrorsToFieldErrors(crossFieldErrors);
            const orderedMappedErrors = mapRuleErrorsToFieldErrors(crossFieldErrors, { prioritizeActivityFields: true });
            const { mergedErrors: mergedRuleErrors } = mergeClearedFieldErrors(errors, visibleFieldOrder, mappedErrors);
            setErrors(mergedRuleErrors);
            const firstCrossFieldError = getFirstErrorInFieldOrder(visibleFieldOrder, {
                ...mergedRuleErrors,
                ...orderedMappedErrors,
            });
            if (firstCrossFieldError?.message || crossFieldErrors[0]?.message) {
                showError(firstCrossFieldError?.message || crossFieldErrors[0]?.message);
            }
            return false;
        }

        setErrors((prevErrors) => omitFieldPathErrors(prevErrors || {}, visibleFieldOrder));
        return true;
    };

    const handleSubmit = () => {
        if (!checkNext()) return;
        setHasError(false);
        const nextStep = applicationForm?.getNextStepForForm?.({
            wizard,
            applicationForm,
            data,
        });
        return handleUpdate({ nextStep });
    };

    return (
        <div data-testid='business-v2-business-activity-prime-step'>
            <h2 style={{ fontWeight: 'bolder' }} className='pt-3'>Business Activity</h2>

            <BusinessV2FieldGrid
                fieldOrder={visibleFieldOrder}
                fieldMap={cyclosFields}
                data={data}
                asLabel={asLabel}
                handleChange={handleChange}
                hasError={hasError}
                onFieldError={handleFieldError}
                dataTestId='business-v2-business-activity-prime-fields'
            />

            {!hideButtons && (
                <div className='mt-4'>
                    <ButtonGroupNew
                        onNextClick={handleSubmit}
                        onBackClick={() => handlePrev?.()}
                        showPageNum
                        pageNum={pageNum}
                        asLabel={false}
                        loading={loading}
                        step={pageNum}
                        isDisabled={asLabel}
                    />
                </div>
            )}
        </div>
    );
};

export default observer(BusinessActivityPrime);
